import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  theme,
  Table,
  Tag,
  Tabs,
  Typography,
  message,
  Alert,
  Button,
  Select,
  InputNumber,
  Space,
  Drawer,
  Spin,
  Modal,
  Descriptions,
} from "antd";
import { MessageOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import AdminTopBar from "../../components/TopBar/AdminTopbar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { AuthContext } from "../../context/AuthContext";
import {
  cancelSmsQueueApi,
  SmsQueueItem,
  SmsQueueStatus,
  SmsQueueType,
  flushSmsQueueApi,
  getSmsQueueApi,
  getSmsQueueByIdApi,
  normalizeSmsQueueItem,
} from "../../services/Admin/SmsQueueApis";

const { Content } = Layout;

const STATUS_TABS: { key: SmsQueueStatus; label: string }[] = [
  { key: "QUEUED", label: "Queued" },
  { key: "SENT", label: "Sent" },
  { key: "FAILED", label: "Failed" },
  { key: "CANCELLED", label: "Cancelled" },
];

const STATUS_COLOR: Record<SmsQueueStatus, string> = {
  QUEUED: "orange",
  SENT: "green",
  FAILED: "red",
  CANCELLED: "default",
};

const SMS_TYPE_OPTIONS: { value: SmsQueueType; label: string }[] = [
  { value: "NEW_POLICY", label: "New Policy" },
  { value: "APPLICATION", label: "Application" },
];

const AdminSmsQueuePage: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [isActive, setActive] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rows, setRows] = useState<SmsQueueItem[]>([]);
  const [smsSendEnabled, setSmsSendEnabled] = useState<boolean | null>(null);
  const [lapseSmsSendEnabled, setLapseSmsSendEnabled] = useState<
    boolean | null
  >(null);
  const [status, setStatus] = useState<SmsQueueStatus>("QUEUED");
  const [smsType, setSmsType] = useState<SmsQueueType | undefined>();
  const [lapseRunId, setLapseRunId] = useState<number | null>(null);
  const [memberId, setMemberId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerItem, setDrawerItem] = useState<SmsQueueItem | null>(null);

  const role = currentUser?.Role?.Name;
  const isSuperAdmin = role === "Super Admin";

  const loadQueue = useCallback(() => {
    setActive(true);
    getSmsQueueApi({
      status,
      ...(smsType ? { smsType } : {}),
      ...(lapseRunId != null ? { lapseRunId } : {}),
      ...(memberId != null ? { memberId } : {}),
    })
      .then((res) => {
        if (!res || res.success === false) {
          message.error(res?.message || "Unable to load SMS queue.");
          setRows([]);
          setSmsSendEnabled(null);
          setLapseSmsSendEnabled(null);
          return;
        }
        const data = res.data ?? {};
        const items = Array.isArray(data.items)
          ? data.items.map(normalizeSmsQueueItem)
          : [];
        setRows(items);
        setSmsSendEnabled(
          typeof data.smsSendEnabled === "boolean"
            ? data.smsSendEnabled
            : null
        );
        setLapseSmsSendEnabled(
          typeof data.lapseSmsSendEnabled === "boolean"
            ? data.lapseSmsSendEnabled
            : null
        );

        // UAT list may omit messageBody — enrich from detail in background
        const missingMessage = items.filter((item) => !item.messageBody);
        if (missingMessage.length === 0) return;

        Promise.all(
          missingMessage.map(async (item) => {
            try {
              const detailRes = await getSmsQueueByIdApi(item.id);
              if (!detailRes || detailRes.success === false || !detailRes.data) {
                return null;
              }
              return normalizeSmsQueueItem({ ...item, ...detailRes.data });
            } catch {
              return null;
            }
          })
        ).then((details) => {
          const byId = new Map(
            details
              .filter((row): row is SmsQueueItem => row != null)
              .map((row) => [row.id, row])
          );
          if (byId.size === 0) return;
          setRows((prev) => prev.map((row) => byId.get(row.id) ?? row));
        });
      })
      .catch((err) => {
        console.log(err);
        message.error("Unable to load SMS queue.");
        setRows([]);
      })
      .finally(() => {
        setActive(false);
        setSelectedIds([]);
      });
  }, [status, smsType, lapseRunId, memberId]);

  useEffect(() => {
    if (role && !isSuperAdmin) {
      navigate("/admin/dashboard");
    }
  }, [role, isSuperAdmin, navigate]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadQueue();
  }, [isSuperAdmin, loadQueue]);

  const openPreview = (record: SmsQueueItem) => {
    setDrawerOpen(true);
    setDrawerItem(record);
    setDrawerLoading(true);
    getSmsQueueByIdApi(record.id)
      .then((res) => {
        if (!res || res.success === false) {
          message.error(res?.message || "Unable to load SMS details.");
          return;
        }
        const detail = (res.data ?? {}) as Record<string, unknown>;
        const merged = normalizeSmsQueueItem({
          ...record,
          ...detail,
          toCellphone:
            detail.toCellphone ||
            detail.toPhone ||
            detail.ToCellphone ||
            record.toCellphone,
          messageBody:
            detail.messageBody ??
            detail.MessageBody ??
            detail.message ??
            record.messageBody,
        });
        setDrawerItem(merged);
        setRows((prev) =>
          prev.map((row) => (row.id === merged.id ? { ...row, ...merged } : row))
        );
      })
      .catch((err) => {
        console.log(err);
        message.error("Unable to load SMS details.");
      })
      .finally(() => setDrawerLoading(false));
  };

  const handleCancelSelected = () => {
    if (selectedIds.length === 0) {
      message.error("Select at least one queued SMS to cancel.");
      return;
    }
    Modal.confirm({
      title: "Cancel selected SMS",
      content: `Mark ${selectedIds.length} queued SMS message(s) as CANCELLED? They will not send on flush.`,
      okText: "Cancel SMS",
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await cancelSmsQueueApi({ ids: selectedIds });
          if (!res || res.success === false) {
            message.error(res?.message || "Unable to cancel SMS messages.");
            return Promise.reject();
          }
          message.success("Selected SMS messages cancelled.");
          loadQueue();
        } catch (err) {
          console.log(err);
          message.error("Unable to cancel SMS messages.");
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleFlushSelected = () => {
    if (selectedIds.length === 0) {
      message.error("Select at least one queued SMS to flush.");
      return;
    }
    Modal.confirm({
      title: "Flush selected SMS",
      content: `Send ${selectedIds.length} queued SMS message(s) now?`,
      okText: "Flush",
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await flushSmsQueueApi({ ids: selectedIds });
          if (!res || res.success === false) {
            message.error(
              res?.message ||
                "Unable to flush SMS. Send may be disabled (409)."
            );
            return Promise.reject();
          }
          message.success("Selected SMS messages flushed.");
          loadQueue();
        } catch (err: any) {
          console.log(err);
          message.error(
            err?.message ||
              "Unable to flush SMS. Send may be disabled (409)."
          );
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleFlushAll = () => {
    Modal.confirm({
      title: "Flush all queued SMS",
      content:
        "Send every QUEUED SMS now? This releases the full review backlog.",
      okText: "Flush all",
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await flushSmsQueueApi({});
          if (!res || res.success === false) {
            message.error(
              res?.message ||
                "Unable to flush SMS. Send may be disabled (409)."
            );
            return Promise.reject();
          }
          message.success("All queued SMS messages flushed.");
          loadQueue();
        } catch (err: any) {
          console.log(err);
          message.error(
            err?.message ||
              "Unable to flush SMS. Send may be disabled (409)."
          );
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<SmsQueueItem> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: SmsQueueStatus) => (
        <Tag color={STATUS_COLOR[value] || "default"}>{value}</Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "smsType",
      render: (value?: string | null) => value || "—",
    },
    {
      title: "To",
      key: "toCellphone",
      render: (_: unknown, row: SmsQueueItem) => row.toCellphone || "—",
    },
    {
      title: "Message",
      key: "messageBody",
      render: (_: unknown, row: SmsQueueItem) => row.messageBody || "—",
      ellipsis: true,
    },
    {
      title: "Member",
      dataIndex: "memberId",
      width: 100,
      render: (value?: number | null) => value ?? "—",
    },
    {
      title: "Group",
      dataIndex: "groupId",
      width: 90,
      render: (value?: number | null) => value ?? "—",
    },
    {
      title: "Lapse run",
      dataIndex: "lapseRunId",
      width: 100,
      render: (value?: number | null) => value ?? "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (value?: string | null) =>
        value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—",
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      ellipsis: true,
      render: (value?: string | null) => value || "—",
    },
  ];

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <LoadingOverlay active={isActive || actionLoading} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  <MessageOutlined
                    style={{ paddingRight: "5px", color: "#f04f05" }}
                  />
                  SMS Queue
                </h2>
                <Space wrap>
                  <Button
                    danger
                    disabled={status !== "QUEUED" || selectedIds.length === 0}
                    onClick={handleCancelSelected}
                  >
                    Cancel selected
                  </Button>
                  <Button
                    type="primary"
                    disabled={status !== "QUEUED" || selectedIds.length === 0}
                    onClick={handleFlushSelected}
                  >
                    Flush selected
                  </Button>
                  <Button
                    danger
                    type="primary"
                    disabled={status !== "QUEUED"}
                    onClick={handleFlushAll}
                  >
                    Flush all queued
                  </Button>
                </Space>
              </div>

              <Typography.Text type="secondary">
                Review queued SMS messages before they send. Open a row to
                preview the message body.
              </Typography.Text>

              {smsSendEnabled === false && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message="SMS send is disabled"
                  description="smsSendEnabled is false. New policy/application SMS messages stay QUEUED for review. Flush will fail with 409 until send is enabled."
                />
              )}
              {smsSendEnabled === true && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="info"
                  showIcon
                  message="SMS send is enabled"
                  description="New policy/application SMS messages send on enqueue. Flush can still release any remaining QUEUED backlog."
                />
              )}
              {lapseSmsSendEnabled === false && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message="Lapse SMS send is disabled"
                  description="lapseSmsSendEnabled is false. Lapse SMS messages stay QUEUED for review until lapse send is enabled."
                />
              )}
              {lapseSmsSendEnabled === true && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="info"
                  showIcon
                  message="Lapse SMS send is enabled"
                  description="Lapse SMS messages send on enqueue. Flush can still release any remaining QUEUED backlog."
                />
              )}

              <Space wrap style={{ marginTop: 16 }}>
                <Select
                  allowClear
                  placeholder="SMS type"
                  style={{ width: 220 }}
                  value={smsType}
                  onChange={(value) => setSmsType(value)}
                  options={SMS_TYPE_OPTIONS}
                />
                <InputNumber
                  placeholder="Lapse run ID"
                  style={{ width: 160 }}
                  min={1}
                  value={lapseRunId ?? undefined}
                  onChange={(value) =>
                    setLapseRunId(typeof value === "number" ? value : null)
                  }
                />
                <InputNumber
                  placeholder="Member ID"
                  style={{ width: 160 }}
                  min={1}
                  value={memberId ?? undefined}
                  onChange={(value) =>
                    setMemberId(typeof value === "number" ? value : null)
                  }
                />
              </Space>

              <Tabs
                type="card"
                style={{ marginTop: 16 }}
                activeKey={status}
                onChange={(key) => setStatus(key as SmsQueueStatus)}
                items={STATUS_TABS.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                }))}
              />

              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 20 }}
                rowSelection={
                  status === "QUEUED"
                    ? {
                        selectedRowKeys: selectedIds,
                        onChange: (keys) =>
                          setSelectedIds(keys.map((k) => Number(k))),
                      }
                    : undefined
                }
                onRow={(record) => ({
                  onClick: (event) => {
                    const target = event.target as HTMLElement;
                    if (
                      target.closest(".ant-checkbox-wrapper") ||
                      target.closest(".ant-table-selection-column")
                    ) {
                      return;
                    }
                    openPreview(record);
                  },
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      <Drawer
        title={
          drawerItem
            ? `SMS #${drawerItem.id}`
            : "SMS preview"
        }
        width={720}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerItem(null);
        }}
      >
        {drawerLoading && !drawerItem ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        ) : drawerItem ? (
          <>
            {drawerLoading ? (
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <Spin size="small" />
              </div>
            ) : null}
            <Descriptions
              size="small"
              column={1}
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[drawerItem.status] || "default"}>
                  {drawerItem.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {drawerItem.smsType || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="To">
                {drawerItem.toCellphone || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Member">
                {drawerItem.memberId ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Group">
                {drawerItem.groupId ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Lapse run">
                {drawerItem.lapseRunId ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {drawerItem.createdAt
                  ? dayjs(drawerItem.createdAt).format("YYYY-MM-DD HH:mm")
                  : "—"}
              </Descriptions.Item>
              {drawerItem.sentAt ? (
                <Descriptions.Item label="Sent">
                  {dayjs(drawerItem.sentAt).format("YYYY-MM-DD HH:mm")}
                </Descriptions.Item>
              ) : null}
              {drawerItem.flushedBy ? (
                <Descriptions.Item label="Flushed by">
                  {drawerItem.flushedBy}
                </Descriptions.Item>
              ) : null}
              {drawerItem.meta ? (
                <Descriptions.Item label="Meta">
                  <Typography.Text code style={{ whiteSpace: "pre-wrap" }}>
                    {drawerItem.meta}
                  </Typography.Text>
                </Descriptions.Item>
              ) : null}
              {drawerItem.errorMessage ? (
                <Descriptions.Item label="Error">
                  {drawerItem.errorMessage}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
            <Typography.Title level={5}>Message body</Typography.Title>
            {drawerItem.messageBody ? (
              <Typography.Paragraph
                style={{
                  whiteSpace: "pre-wrap",
                  padding: 12,
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  background: "#fafafa",
                }}
              >
                {drawerItem.messageBody}
              </Typography.Paragraph>
            ) : (
              <Typography.Text type="secondary">
                No message body available.
              </Typography.Text>
            )}
          </>
        ) : (
          <Typography.Text type="secondary">
            Unable to load SMS details.
          </Typography.Text>
        )}
      </Drawer>
    </LoadingOverlay>
  );
};

export default AdminSmsQueuePage;
